/**
 * Notification centre strings (`notifs.*`): inbox, announcements, sent history, preferences, bell dropdown.
 * Registered in `extra.ts`; the English object is typed against the Polish one, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const notifsPl = {
  nav: 'Powiadomienia',
  title: 'Powiadomienia',
  subtitle: 'Centrum komunikacji i alertów systemowych',
  newNotification: 'Nowe powiadomienie',
  loadError: 'Nie udało się wczytać powiadomień.',
  loadMore: 'Załaduj więcej',
  shown: 'Wyświetlono {shown} z {total}',
  tabs: { inbox: 'Skrzynka', sent: 'Wysłane', alerts: 'Alerty sprzedaży' },
  category: {
    ALERT: 'Alerty',
    TASK: 'Zadania',
    APPROVAL: 'Akceptacje',
    LEAD: 'Leady',
    ANNOUNCEMENT: 'Ogłoszenia',
    SYSTEM: 'Systemowe',
  },
  priority: { HIGH: 'Wysoki', MEDIUM: 'Średni', LOW: 'Niski', urgent: 'Pilne' },
  filters: {
    category: 'Kategoria',
    all: 'Wszystkie',
    search: 'Szukaj w powiadomieniach…',
    unread: 'Nieprzeczytane',
    pinned: 'Przypięte',
    confirm: 'Do potwierdzenia',
    active: 'Aktywne filtry',
    clearAll: 'Wyczyść filtry',
  },
  group: {
    byDay: 'Po dniach',
    byCategory: 'Po kategoriach',
    pinned: 'Przypięte',
    today: 'Dzisiaj',
    yesterday: 'Wczoraj',
    week: 'Ostatnie 7 dni',
    older: 'Starsze',
  },
  item: {
    from: 'od {name}',
    open: 'Otwórz',
    more: 'Pokaż całość',
    pin: 'Przypnij',
    unpin: 'Odepnij',
    pinned: 'Przypięte',
    markRead: 'Oznacz jako przeczytane',
    markUnread: 'Oznacz jako nieprzeczytane',
    delete: 'Usuń',
    confirm: 'Potwierdzam przeczytanie',
    confirmed: 'Potwierdzono',
    needsConfirm: 'Wymaga potwierdzenia',
    unreadDot: 'Nieprzeczytane',
  },
  bulk: {
    toggle: 'Zaznacz',
    toolbar: 'Akcje zbiorcze',
    select: 'Zaznacz powiadomienie',
    selectAll: 'Zaznacz wszystkie',
    selected: 'Zaznaczono: {count}',
    read: 'Przeczytane',
    unread: 'Nieprzeczytane',
    pin: 'Przypnij',
    delete: 'Usuń',
    exit: 'Zakończ zaznaczanie',
  },
  empty: {
    title: 'Brak powiadomień',
    body: 'Tutaj pojawią się alerty, zadania, akceptacje i ogłoszenia.',
    filtered: 'Brak wyników dla wybranych filtrów',
    filteredBody: 'Zmień lub wyczyść filtry, aby zobaczyć więcej powiadomień.',
    unread: 'Wszystko przeczytane',
    pinned: 'Brak przypiętych powiadomień',
    confirm: 'Nic nie czeka na potwierdzenie',
  },
  hero: {
    unread: 'Nieprzeczytane',
    unreadHigh: 'w tym pilnych: {count}',
    ofAll: 'z {total} wszystkich',
    pinned: 'Przypięte',
    pinnedHint: 'ważne na górze listy',
    confirm: 'Do potwierdzenia',
    confirmHint: 'wymagają Twojej reakcji',
    confirmNone: 'nic nie czeka',
    today: 'Dzisiaj',
    todayHint: 'nowe powiadomienia',
    markAll: 'Oznacz wszystkie jako przeczytane',
    openAlerts: 'Alerty sprzedaży',
    show: 'Pokaż',
    showUnread: 'Pokaż nieprzeczytane',
    insightConfirm: 'Wiadomości wymagające potwierdzenia przeczytania: {count}.',
    insightHigh: 'Pilne nieprzeczytane powiadomienia: {count}. Zajrzyj do nich w pierwszej kolejności.',
    insightUnread: 'Nieprzeczytane powiadomienia: {count}.',
    insightClear: 'Wszystko przeczytane — skrzynka jest czysta.',
  },
  prefs: {
    title: 'Ustawienia',
    intro: 'Zdecyduj, jakie powiadomienia mają do Ciebie docierać.',
    dnd: 'Nie przeszkadzaj',
    dndHint: 'Powiadomienia trafiają do skrzynki, ale bez push i e-maila.',
    muted: 'Wycisz kategorie',
    alwaysArrive: 'Powiadomienia o wysokim priorytecie oraz wymagające potwierdzenia zawsze do Ciebie dotrą, nawet gdy kategoria jest wyciszona.',
  },
  bell: { viewAll: 'Zobacz wszystkie' },
  announce: {
    title: 'Nowe powiadomienie',
    description: 'Wyślij ogłoszenie do wybranych osób lub grup.',
    recipients: 'Odbiorcy',
    titleLabel: 'Tytuł',
    titlePlaceholder: 'Krótki, konkretny tytuł',
    bodyLabel: 'Treść',
    bodyPlaceholder: 'Napisz, czego dotyczy wiadomość…',
    category: 'Kategoria',
    priority: 'Priorytet',
    link: 'Link (opcjonalnie)',
    linkHint: 'Ścieżka w aplikacji zaczynająca się od / albo adres https://',
    push: 'Push',
    pushHint: 'Powiadomienie na telefon (jeśli odbiorca ma subskrypcję)',
    email: 'E-mail',
    emailHint: 'Kopia wiadomości na adres e-mail',
    requiresConfirm: 'Wymagaj potwierdzenia',
    requiresConfirmHint: 'Odbiorca musi potwierdzić przeczytanie',
    pin: 'Przypnij',
    pinHint: 'Na górze skrzynki każdego odbiorcy',
    cancel: 'Anuluj',
    send: 'Wyślij',
    sent: 'Wysłano do {count} osób',
    sentOne: 'Wysłano do 1 osoby',
    sentMuted: '({muted} wyciszone)',
    failed: 'Nie udało się wysłać powiadomienia.',
    errAudience: 'Wybierz co najmniej jednego odbiorcę.',
    errTitle: 'Podaj tytuł.',
    errTitleLong: 'Tytuł jest za długi.',
    errBody: 'Podaj treść.',
    errBodyLong: 'Treść jest za długa.',
    errLink: 'Podaj ścieżkę zaczynającą się od / albo adres https://.',
  },
  recipients: {
    groups: 'Grupy',
    people: 'Osoby',
    all: 'Wszyscy użytkownicy',
    myTeam: 'Mój zespół',
    counts: '{users} os. · {push} push',
    estimate: 'Szacunkowo do {users} os. ({push} z push)',
    searchPeople: 'Szukaj osoby…',
    noPeople: 'Nikogo nie znaleziono.',
    none: 'Nie wybrano odbiorców.',
    selected: 'Wybrani odbiorcy',
    push: 'push',
    hasPush: 'Ma aktywną subskrypcję push',
  },
  sent: {
    details: 'Szczegóły wysyłki',
    empty: 'Nic jeszcze nie wysłano',
    emptyBody: 'Wysłane ogłoszenia pojawią się tutaj wraz ze statusem przeczytania.',
    loadError: 'Nie udało się wczytać historii wysyłek.',
    prev: 'Poprzednia strona',
    next: 'Następna strona',
    read: 'Przeczytane',
    confirmed: 'Potwierdzone',
    readAt: 'Przeczytano {when}',
    unreadStatus: 'Nieprzeczytane',
    recipientsLine: 'Odbiorców: {count} · dostarczono: {delivered} · wyciszone: {muted}',
    filterAll: 'Wszyscy',
    filterUnread: 'Nieprzeczytane',
    filterPending: 'Bez potwierdzenia',
    noStatuses: 'Brak osób dla tego filtra.',
  },
};

export type NotifsMessages = Shape<typeof notifsPl>;

export const notifsEn: NotifsMessages = {
  nav: 'Notifications',
  title: 'Notifications',
  subtitle: 'Communication and system alerts centre',
  newNotification: 'New notification',
  loadError: 'Could not load notifications.',
  loadMore: 'Load more',
  shown: 'Showing {shown} of {total}',
  tabs: { inbox: 'Inbox', sent: 'Sent', alerts: 'Sales alerts' },
  category: {
    ALERT: 'Alerts',
    TASK: 'Tasks',
    APPROVAL: 'Approvals',
    LEAD: 'Leads',
    ANNOUNCEMENT: 'Announcements',
    SYSTEM: 'System',
  },
  priority: { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low', urgent: 'Urgent' },
  filters: {
    category: 'Category',
    all: 'All',
    search: 'Search notifications…',
    unread: 'Unread',
    pinned: 'Pinned',
    confirm: 'To confirm',
    active: 'Active filters',
    clearAll: 'Clear filters',
  },
  group: {
    byDay: 'By day',
    byCategory: 'By category',
    pinned: 'Pinned',
    today: 'Today',
    yesterday: 'Yesterday',
    week: 'Last 7 days',
    older: 'Older',
  },
  item: {
    from: 'from {name}',
    open: 'Open',
    more: 'Show all',
    pin: 'Pin',
    unpin: 'Unpin',
    pinned: 'Pinned',
    markRead: 'Mark as read',
    markUnread: 'Mark as unread',
    delete: 'Delete',
    confirm: 'I confirm I have read this',
    confirmed: 'Confirmed',
    needsConfirm: 'Confirmation required',
    unreadDot: 'Unread',
  },
  bulk: {
    toggle: 'Select',
    toolbar: 'Bulk actions',
    select: 'Select notification',
    selectAll: 'Select all',
    selected: 'Selected: {count}',
    read: 'Read',
    unread: 'Unread',
    pin: 'Pin',
    delete: 'Delete',
    exit: 'Exit selection',
  },
  empty: {
    title: 'No notifications',
    body: 'Alerts, tasks, approvals and announcements will show up here.',
    filtered: 'No results for the selected filters',
    filteredBody: 'Change or clear the filters to see more notifications.',
    unread: 'All caught up',
    pinned: 'No pinned notifications',
    confirm: 'Nothing is waiting for confirmation',
  },
  hero: {
    unread: 'Unread',
    unreadHigh: '{count} of them urgent',
    ofAll: 'of {total} in total',
    pinned: 'Pinned',
    pinnedHint: 'important, kept on top',
    confirm: 'To confirm',
    confirmHint: 'need your response',
    confirmNone: 'nothing waiting',
    today: 'Today',
    todayHint: 'new notifications',
    markAll: 'Mark all as read',
    openAlerts: 'Sales alerts',
    show: 'Show',
    showUnread: 'Show unread',
    insightConfirm: 'Messages that need a read confirmation: {count}.',
    insightHigh: 'Urgent unread notifications: {count}. Take a look at them first.',
    insightUnread: 'Unread notifications: {count}.',
    insightClear: 'All caught up — your inbox is clear.',
  },
  prefs: {
    title: 'Settings',
    intro: 'Choose which notifications should reach you.',
    dnd: 'Do not disturb',
    dndHint: 'Notifications land in the inbox, but without push or e-mail.',
    muted: 'Mute categories',
    alwaysArrive: 'High-priority notifications and those requiring confirmation always reach you, even when their category is muted.',
  },
  bell: { viewAll: 'View all' },
  announce: {
    title: 'New notification',
    description: 'Send an announcement to selected people or groups.',
    recipients: 'Recipients',
    titleLabel: 'Title',
    titlePlaceholder: 'A short, specific title',
    bodyLabel: 'Message',
    bodyPlaceholder: 'Write what this is about…',
    category: 'Category',
    priority: 'Priority',
    link: 'Link (optional)',
    linkHint: 'An in-app path starting with / or an https:// address',
    push: 'Push',
    pushHint: 'Phone notification (if the recipient is subscribed)',
    email: 'E-mail',
    emailHint: 'A copy of the message by e-mail',
    requiresConfirm: 'Require confirmation',
    requiresConfirmHint: 'The recipient must confirm reading',
    pin: 'Pin',
    pinHint: 'On top of every recipient’s inbox',
    cancel: 'Cancel',
    send: 'Send',
    sent: 'Sent to {count} people',
    sentOne: 'Sent to 1 person',
    sentMuted: '({muted} muted)',
    failed: 'Could not send the notification.',
    errAudience: 'Choose at least one recipient.',
    errTitle: 'Enter a title.',
    errTitleLong: 'The title is too long.',
    errBody: 'Enter a message.',
    errBodyLong: 'The message is too long.',
    errLink: 'Enter a path starting with / or an https:// address.',
  },
  recipients: {
    groups: 'Groups',
    people: 'People',
    all: 'All users',
    myTeam: 'My team',
    counts: '{users} people · {push} push',
    estimate: 'Up to {users} people ({push} with push)',
    searchPeople: 'Search people…',
    noPeople: 'Nobody found.',
    none: 'No recipients selected.',
    selected: 'Selected recipients',
    push: 'push',
    hasPush: 'Has an active push subscription',
  },
  sent: {
    details: 'Delivery details',
    empty: 'Nothing sent yet',
    emptyBody: 'Sent announcements will appear here together with their read status.',
    loadError: 'Could not load the sent history.',
    prev: 'Previous page',
    next: 'Next page',
    read: 'Read',
    confirmed: 'Confirmed',
    readAt: 'Read {when}',
    unreadStatus: 'Unread',
    recipientsLine: 'Recipients: {count} · delivered: {delivered} · muted: {muted}',
    filterAll: 'Everyone',
    filterUnread: 'Unread',
    filterPending: 'Not confirmed',
    noStatuses: 'Nobody matches this filter.',
  },
};
